<script lang="ts" setup>
const { t: $t } = useI18n()

import type { CustomerModuleCommentCreateOrUpdatePayload } from '../../types'
import { CommentVisibility } from '~/prisma/enums'
import type { UpdateComment, UpdateOrCreateComment } from '~/types'

const props = defineProps<{ payload?: CustomerModuleCommentCreateOrUpdatePayload, width?: string }>()
const emits = defineEmits<{ (e: 'close'): void }>()

const { commentQuery } = useCustomerModuleQuery()
const { data: comment, isLoading: isCommentLoading, isFetching: isCommentFetching } = commentQuery.getCommentById(computed(() => props.payload?.mode === 'update' ? props.payload.id : undefined))

const payload = ref<UpdateOrCreateComment | undefined>(undefined)
watch([() => props.payload, comment, isCommentFetching], () => {
  if (payload.value || !props.payload) {
    return
  }

  if (props.payload.mode === 'create') {
    payload.value = {
      mode: 'create',
      data: {
        type: props.payload.type,
        visibility: CommentVisibility.Public,
        visibleToIds: [],
        text: '',
        files: [],
        [commentConfigs[props.payload.type].commentTableRelationFieldName]: props.payload.entityId,
      },
    }
  }

  if (comment.value && !isCommentFetching.value) {
    payload.value = {
      mode: 'update',
      data: {
        ...comment.value,
        visibleToIds: comment.value.visibleTo.map(user => user.id),
      },
    } satisfies UpdateComment
  }
}, { immediate: true })

const type = computed(() => payload.value?.data.type)
const entityId = computed(() => {
  if (!payload.value) {
    return undefined
  }
  return payload.value.data[commentConfigs[payload.value.data.type].commentTableRelationFieldName] ?? undefined
})
const { userOptions, isSupportRestrictedToSpecificUser } = useCommentVisibleToOptions(type, entityId)

const isReply = computed(() => !!(payload.value?.mode === 'create'
  ? payload.value.data.originalCommentId
  : comment.value?.originalCommentId))

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const create = useMutation({
  mutationFn: $trpc.customerModule.comment.create.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.comment.notification.create.error') }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'comments', 'getAll'] }),
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'comments', 'getCount'] }),
    ])

    notification.success({ title: $t('customerModule.comment.notification.create.success'), duration: 4500 })
    emits('close')
  },
})

const update = useMutation({
  mutationFn: $trpc.customerModule.comment.update.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.comment.notification.update.error') }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'comments', 'getAll'] }),
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'comments', 'getCount'] }),
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'comments', 'getCommentById'] }),
    ])

    notification.success({ title: $t('customerModule.comment.notification.update.success'), duration: 4500 })
    emits('close')
  },
})

const isMutating = computed(() => create.isPending.value || update.isPending.value)

function save(payload: UpdateOrCreateComment) {
  if (payload.mode === 'create') {
    return create.mutateAsync(payload.data)
  } else {
    return update.mutateAsync(payload.data)
  }
}
</script>

<template>
  <CustomerModulePopup
    :show="!!props.payload"
    :width
    :is-loading="isCommentLoading"
    :is-spinning="isMutating"
    :title="props.payload?.mode === 'update' ? $t('customerModule.comment.update') : $t('customerModule.comment.create')"
    @close="$emit('close')"
  >
    <CommentCreateOrEditForm
      v-if="payload"
      :payload="payload"
      :is-reply
      :user-options
      :is-support-restricted-to-specific-user
      @save="save"
      @close="$emit('close')"
    />
  </CustomerModulePopup>
</template>
